import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntdLayout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  message,
  Space,
  Typography,
  Drawer,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  MenuOutlined,
  SearchOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { getUserInfo, clearAuthData, formatUserDisplayName, UserInfo } from '../../utils/auth';
import { logout } from '../../services/auth';
import { UserRole } from '../../constants/user';
import { useResponsive } from '../../hooks/useResponsive';
import MobileBottomNav from '../MobileBottomNav';

const { Header, Sider, Content } = AntdLayout;
const { Text } = Typography;

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );
  const [userInfo, setUserInfo] = useState<UserInfo | null>(getUserInfo());
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userInfo?.role === UserRole.ADMIN;
  const { isMobile } = useResponsive();

  // Refresh user info from server
  const refreshUserInfo = async () => {
    try {
      const { getCurrentUser } = await import('../../services/auth');
      const { setUserInfo: saveUserInfo } = await import('../../utils/auth');
      const freshUserInfo = await getCurrentUser();
      saveUserInfo(freshUserInfo);
      setUserInfo(freshUserInfo);
    } catch (error) {
      // 如果获取失败，使用本地缓存的信息
    }
  };

  // Refresh user info on mount
  useEffect(() => {
    refreshUserInfo();
  }, []);

  // Menu items based on user role
  const menuItems = [];

  // 财务人员只能看到财务管理
  if (userInfo?.role === UserRole.FINANCE) {
    menuItems.push({
      key: '/finance',
      icon: <DollarOutlined />,
      label: '财务管理',
    });
  } else if (userInfo?.role === UserRole.LAWYER) {
    // 律师只能看到案件相关模块
    menuItems.push(
      {
        key: '/workspace',
        icon: <BookOutlined />,
        label: '工作台',
      },
      {
        key: '/cases',
        icon: <FileTextOutlined />,
        label: '案件管理',
      },
      {
        key: 'delilegal',
        icon: <SearchOutlined />,
        label: '得理法搜',
      }
    );
  } else {
    // 普通员工和管理员可以看到业务模块
    menuItems.push(
      {
        key: '/workspace',
        icon: <BookOutlined />,
        label: '工作台',
      },
      {
        key: '/leads',
        icon: <UserOutlined />,
        label: '线索管理',
      },
      {
        key: '/cases',
        icon: <FileTextOutlined />,
        label: '案件管理',
      },
      {
        key: 'delilegal',
        icon: <SearchOutlined />,
        label: '得理法搜',
      }
    );

    // 管理员额外可以看到财务管理、数据看板、资源库和配置中心
    if (isAdmin) {
      menuItems.push(
        {
          key: '/finance',
          icon: <DollarOutlined />,
          label: '财务管理',
        },
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '数据看板',
        },
        {
          key: 'resources',
          icon: <FolderOutlined />,
          label: '资源库',
          children: [
            {
              key: '/resources/copywriting',
              label: '文案库',
            },
            {
              key: '/resources/materials',
              label: '素材库',
            },
          ],
        },
        {
          key: '/config',
          icon: <SettingOutlined />,
          label: '配置中心',
        }
      );
    } else {
      // 普通员工也可以访问资源库（个人资源）
      menuItems.push({
        key: 'resources',
        icon: <FolderOutlined />,
        label: '资源库',
        children: [
          {
            key: '/resources/copywriting',
            label: '文案库',
          },
          {
            key: '/resources/materials',
            label: '素材库',
          },
        ],
      });
    }
  }

  // Handle menu click
  const handleMenuClick = ({ key }: { key: string }) => {
    // Handle external link for 得理法搜
    if (key === 'delilegal') {
      window.open('https://www.delilegal.com/ai', '_blank');
      return;
    }
    navigate(key);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      clearAuthData();
      message.success('退出登录成功');
      navigate('/login');
    } catch (error) {
      // Even if logout API fails, clear local data and redirect
      clearAuthData();
      navigate('/login');
    }
  };

  // User dropdown menu
  const userMenuItems: any[] = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: '个人资料',
    //   onClick: () => {
    //     message.info('个人资料功能开发中...');
    //   },
    // },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: '系统设置',
    //   onClick: () => {
    //     message.info('系统设置功能开发中...');
    //   },
    // },
    // {
    //   type: 'divider' as const,
    // },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <AntdLayout className="layout-container">
      {/* Header */}
      <Header className="layout-header" style={{ padding: isMobile ? '0 12px' : '0 24px' }}>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            style={{ fontSize: '18px' }}
          />
        )}
        
        <div className="layout-logo">
          <div className="layout-brand-mark">瑞</div>
          {!isMobile && <span>瑞象CRM</span>}
        </div>

        <div className="layout-user-info">
          <Space size="middle">
            {/* User dropdown */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={userInfo?.avatar}
                />
                {!isMobile && <Text>{formatUserDisplayName(userInfo)}</Text>}
              </Space>
            </Dropdown>
          </Space>
        </div>
      </Header>

      <AntdLayout className="layout-body">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sider
            className="layout-sider"
            collapsible
            collapsed={collapsed}
            onCollapse={(val) => {
              setCollapsed(val);
              localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(val));
            }}
            trigger={null}
            width={200}
            collapsedWidth={60}
          >
            {/* Collapse toggle */}
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => {
                  const next = !collapsed;
                  setCollapsed(next);
                  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
                }}
                style={{ fontSize: '16px', width: 32, height: 32 }}
              />
            </div>

            {/* Navigation menu */}
            <Menu
              mode="vertical"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              triggerSubMenuAction="hover"
              style={{ borderRight: 0 }}
            />
          </Sider>
        )}

        {/* Mobile Menu Drawer */}
        {isMobile && (
          <Drawer
            title="菜单"
            placement="left"
            onClose={() => setMobileMenuVisible(false)}
            open={mobileMenuVisible}
            width={250}
          >
            <Menu
              mode="vertical"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={(e) => {
                handleMenuClick(e);
                setMobileMenuVisible(false);
              }}
              triggerSubMenuAction="hover"
              style={{ borderRight: 0 }}
            />
          </Drawer>
        )}

        {/* Main content */}
        <Content className={isMobile ? "layout-content mobile-layout-content" : "layout-content"}>
          <Outlet />
        </Content>
      </AntdLayout>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </AntdLayout>
  );
};

export default Layout;